import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label'; // Usuário verificará este caminho
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { SpecialEventRow, SpecialEventConfig, SpecialEvent, XPMultiplierEventConfig, MissionUnlockEventConfig } from '@/lib/gamification/types';
import { createSpecialEvent, updateSpecialEvent } from '@/lib/gamification/missionService';
import { toast } from 'react-hot-toast';
import { Loader2, Save } from 'lucide-react';
import { Json } from '../../../types/supabase'; // Caminho ajustado

const EVENT_TYPES = ['XP_MULTIPLIER', 'MISSION_UNLOCK']; // Adicionar mais tipos se houver

interface SpecialEventFormProps {
  initialData?: SpecialEventRow | null;
  onSave: (event: SpecialEvent) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

type EventFormDataType = Partial<Omit<SpecialEventRow, 'id' | 'created_at' | 'updated_at' | 'config'>> & {
  config_string?: string; 
};

const SpecialEventForm: React.FC<SpecialEventFormProps> = ({ initialData, onSave, onCancel, isLoading }) => {
  const [formData, setFormData] = useState<EventFormDataType>({
    event_key: '',
    title: '',
    description: '',
    event_type: EVENT_TYPES[0],
    config_string: '{\\"xp_multiplier\\": 1.5, \\"affected_actions\\": [\\"all\\"]}',
    start_date: '',
    end_date: '',
    is_active: true,
    target_user_ids: null,
    required_badge_ids: null,
    banner_url: null,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        config_string: JSON.stringify(initialData.config || {}, null, 2),
        start_date: initialData.start_date ? initialData.start_date.split('T')[0] : '',
        end_date: initialData.end_date ? initialData.end_date.split('T')[0] : '',
        target_user_ids: initialData.target_user_ids || null,
        required_badge_ids: initialData.required_badge_ids || null,
      });
    } else {
      // Atualiza o config_string exemplo quando não há initialData (modo de criação)
      // e o event_type muda.
      const exampleConfig = formData.event_type === 'XP_MULTIPLIER' 
        ? '{\\n  \\"xp_multiplier\\": 1.5,\\n  \\"affected_actions\\": [\\"all\\"]\\n}' 
        : '{\\n  \\"unlocks_mission_keys\\": [\\"mission_key_1\\"]\\n}';
      setFormData(prev => ({ // Usar prev para evitar stale closure com formData.event_type
        ...prev,
        event_key: '',
        title: '',
        description: '',
        // event_type já está no prev e será mantido
        config_string: exampleConfig, 
        start_date: '',
        end_date: '',
        is_active: true,
        target_user_ids: null,
        required_badge_ids: null,
        banner_url: null,
      }));
    }
  }, [initialData, formData.event_type]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else if (name === 'target_user_ids' || name === 'required_badge_ids') {
      setFormData(prev => ({ ...prev, [name]: value ? value.split(',').map(s => s.trim()).filter(s => s) : null }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleConfigChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, config_string: e.target.value }));
  };

  // Efeito para atualizar o config_string de exemplo de forma mais controlada ao criar novo evento
  useEffect(() => {
    if (!initialData) { // Apenas no modo de criação
        let newConfigString = '';
        if (formData.event_type === 'XP_MULTIPLIER') {
            newConfigString = '{\\n  \\"xp_multiplier\\": 1.5,\\n  \\"affected_actions\\": [\\"all\\"]\\n}';
        } else if (formData.event_type === 'MISSION_UNLOCK') {
            newConfigString = '{\\n  \\"unlocks_mission_keys\\": [\\"mission_key_1\\", \\"mission_key_2\\"]\\n}';
        }
        
        // Verifica se o config_string atual é um dos exemplos padrões ou vazio antes de sobrescrever
        const defaultXPConfigExample = '{\\"xp_multiplier\\": 1.5, \\"affected_actions\\": [\\"all\\"]}';
        const defaultMissionUnlockConfigExample = '{\\"unlocks_mission_keys\\\": [\\"mission_key_1\\"]}'; // Exemplo anterior
        const currentFormattedXPExample = '{\\n  \\"xp_multiplier\\": 1.5,\\n  \\"affected_actions\\": [\\"all\\"]\\n}';
        const currentFormattedMissionExample = '{\\n  \\"unlocks_mission_keys\\": [\\"mission_key_1\\", \\"mission_key_2\\"]\\n}';


        if (formData.config_string === defaultXPConfigExample || 
            formData.config_string === defaultMissionUnlockConfigExample ||
            formData.config_string === currentFormattedXPExample ||
            formData.config_string === currentFormattedMissionExample ||
            !formData.config_string?.trim() || 
            formData.config_string === '{}') {
             setFormData(prev => ({...prev, config_string: newConfigString}));
        }
    }
  }, [formData.event_type, initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    let parsedConfig: SpecialEventConfig;
    try {
      parsedConfig = JSON.parse(formData.config_string || '{}'); // Default to empty object if string is empty
      if (formData.event_type === 'XP_MULTIPLIER') {
        const xpConfig = parsedConfig as XPMultiplierEventConfig;
        if (typeof xpConfig.xp_multiplier !== 'number' || !Array.isArray(xpConfig.affected_actions)) {
          throw new Error('Config para XP_MULTIPLIER inválida. Deve ter xp_multiplier (number) e affected_actions (array).');
        }
      } else if (formData.event_type === 'MISSION_UNLOCK') {
        const missionConfig = parsedConfig as MissionUnlockEventConfig;
        if (!Array.isArray(missionConfig.unlocks_mission_keys)) {
          throw new Error('Config para MISSION_UNLOCK inválida. Deve ter unlocks_mission_keys (array).');
        }
      }
    } catch (error: any) {
      setFormError(`Erro no formato JSON da Configuração: ${error.message}`);
      setIsSubmitting(false);
      toast.error(`JSON inválido para Configuração: ${error.message}`);
      return;
    }

    const payloadStartDate = formData.start_date ? new Date(formData.start_date).toISOString() : '';
    const payloadEndDate = formData.end_date ? new Date(formData.end_date).toISOString() : '';

    if (!payloadStartDate || !payloadEndDate) {
        toast.error('As datas de início e fim são obrigatórias.');
        setFormError('As datas de início e fim são obrigatórias.');
        setIsSubmitting(false);
        return;
    }
    if (!formData.event_key?.trim() || !formData.title?.trim()) {
        toast.error('Chave do Evento e Título são obrigatórios.');
        setFormError('Chave do Evento e Título são obrigatórios.');
        setIsSubmitting(false);
        return;
    }

    const eventPayload: Omit<SpecialEventRow, 'id' | 'created_at' | 'updated_at'> = {
      event_key: formData.event_key || '',
      title: formData.title || '',
      description: formData.description || '',
      event_type: formData.event_type || EVENT_TYPES[0],
      config: parsedConfig as unknown as Json,
      start_date: payloadStartDate,
      end_date: payloadEndDate,
      is_active: formData.is_active === undefined ? true : formData.is_active,
      target_user_ids: formData.target_user_ids || null,
      required_badge_ids: formData.required_badge_ids || null,
      banner_url: formData.banner_url || null,
    };

    try {
      let savedEvent: SpecialEvent | null = null;
      if (initialData?.id) {
        savedEvent = await updateSpecialEvent(supabase, initialData.id, eventPayload);
      } else {
        savedEvent = await createSpecialEvent(supabase, eventPayload);
      }

      if (savedEvent) {
        toast.success(`Evento ${initialData?.id ? 'atualizado' : 'criado'} com sucesso!`);
        onSave(savedEvent);
      } else {
        const errorMsg = `Falha ao ${initialData?.id ? 'atualizar' : 'criar'} o evento. Verifique os logs do console.`;
        setFormError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (error: any) {
      console.error('Erro ao salvar evento:', error);
      const message = error.message || 'Ocorreu um erro desconhecido ao salvar o evento.';
      setFormError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>{initialData?.id ? 'Editar Evento Especial' : 'Criar Novo Evento Especial'}</CardTitle>
        <CardDescription>
          {initialData?.id ? 'Modifique os detalhes do evento.' : 'Preencha os detalhes para um novo evento.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="event_key">Chave do Evento (única)</Label>
              <Input id="event_key" name="event_key" value={formData.event_key || ''} onChange={handleChange} required />
            </div>
            <div>
              <Label htmlFor="title">Título</Label>
              <Input id="title" name="title" value={formData.title || ''} onChange={handleChange} required />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Descrição</Label>
            <Textarea id="description" name="description" value={formData.description || ''} onChange={handleChange} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="event_type">Tipo de Evento</Label>
              <select 
                id="event_type" 
                name="event_type" 
                value={formData.event_type || ''} 
                onChange={handleChange}
                className="w-full mt-1 p-2 border rounded-md bg-white dark:bg-gray-800 dark:border-gray-700"
                required
              >
                {EVENT_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
             <div>
              <Label htmlFor="banner_url">URL do Banner (Opcional)</Label>
              <Input id="banner_url" name="banner_url" value={formData.banner_url || ''} onChange={handleChange} placeholder="https://example.com/banner.png" />
            </div>
          </div>
          
          <div>
            <Label htmlFor="config_string">Configuração do Evento (JSON)</Label>
            <Textarea
              id="config_string"
              name="config_string"
              value={formData.config_string || ''}
              onChange={handleConfigChange}
              rows={8}
              placeholder={'Ex: {\\"xp_multiplier\\": 2, \\"affected_actions\\": [\\"create_post\\"]}'}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              A estrutura do JSON depende do Tipo de Evento.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Data de Início</Label>
              <Input id="start_date" name="start_date" type="datetime-local" value={formData.start_date || ''} onChange={handleChange} required />
            </div>
            <div>
              <Label htmlFor="end_date">Data de Fim</Label>
              <Input id="end_date" name="end_date" type="datetime-local" value={formData.end_date || ''} onChange={handleChange} required />
            </div>
          </div>
          
          <div>
            <Label htmlFor="target_user_ids">IDs de Usuários Alvo (separados por vírgula, opcional)</Label>
            <Input 
              id="target_user_ids" 
              name="target_user_ids" 
              value={Array.isArray(formData.target_user_ids) ? formData.target_user_ids.join(', ') : (formData.target_user_ids || '')} 
              onChange={handleChange} 
              placeholder="uuid1, uuid2, uuid3" />
          </div>
          <div>
            <Label htmlFor="required_badge_ids">IDs de Badges Requeridas (separados por vírgula, opcional)</Label>
            <Input 
              id="required_badge_ids" 
              name="required_badge_ids" 
              value={Array.isArray(formData.required_badge_ids) ? formData.required_badge_ids.join(', ') : (formData.required_badge_ids || '')} 
              onChange={handleChange} 
              placeholder="badge_uuid1, badge_uuid2" />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox id="is_active" name="is_active" checked={formData.is_active || false} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: !!checked }))} />
            <Label htmlFor="is_active" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Evento Ativo
            </Label>
          </div>

          {formError && <p className="text-sm text-red-600">{formError}</p>}

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting || isLoading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting || isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {initialData?.id ? 'Salvar Alterações' : 'Criar Evento'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default SpecialEventForm; 